import React, { ReactNode, useId } from 'react';
import { Button, ModalBody, ModalFooter, ModalHeader, ModalProps, Modal as RSModal } from 'reactstrap';

interface ComponentProps extends ModalProps {
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  header?: ReactNode;
  isOpen?: boolean;
  footer?: ReactNode;
  setIsOpen: (toggle: boolean) => void;
  includeFooter?: boolean;
  closeButtonText?: string;
  // When true, removes every close affordance (header X and the default footer close button) — e.g. while a
  // long-running operation must not be interrupted. The caller controls when it flips.
  disableClose?: boolean;
}

const Modal: React.FC<ComponentProps> = ({
  isOpen,
  setIsOpen,
  children,
  header,
  bodyClassName,
  headerClassName,
  footerClassName,
  footer,
  closeButtonText = 'Close',
  includeFooter = true,
  disableClose = false,
  ...props
}) => {
  // `role="dialog"` with no accessible name is an axe violation (aria-dialog-name), and reactstrap
  // puts the role on its own element - so the name has to be pointed at the header rather than set
  // on anything a caller renders. A caller passing its own `labelledBy` or `aria-label` still wins,
  // since the spread lands after this.
  const headerId = `${useId()}-modal-header`;

  return (
    <RSModal isOpen={isOpen} labelledBy={header ? headerId : undefined} {...props}>
      {header && (
        <ModalHeader
          id={headerId}
          className={headerClassName ?? 'header'}
          // Omitting `toggle` makes reactstrap render no close (X) button.
          toggle={
            disableClose
              ? undefined
              : () => {
                  setIsOpen(!isOpen);
                }
          }
        >
          {header}
        </ModalHeader>
      )}
      <ModalBody className={bodyClassName}>{children}</ModalBody>
      {includeFooter && (
        <ModalFooter className={footerClassName}>
          {footer ? (
            footer
          ) : (
            !disableClose && (
              <Button color="secondary" onClick={() => setIsOpen(!isOpen)}>
                {closeButtonText}
              </Button>
            )
          )}
        </ModalFooter>
      )}
    </RSModal>
  );
};

export default Modal;
